import React, { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/lib/AuthContext";
import { logActivity } from "@/lib/activityLog";
import { ModalShell, Field, TextInput, SelectInput, ErrorBanner } from "../shared/FormElements";

const STEPS = ["Project", "Objective", "Metrics", "Goals", "Team"];

// presetClientId: when opened via "Save & add project" from Add Client, the client is
// already known, this pre-selects it in step 1 but the step still shows (project name
// is still needed).
export default function AddProjectWizard({ presetClientId, onClose, onSaved }) {
  const { profile } = useAuth();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [clients, setClients] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [masterMetrics, setMasterMetrics] = useState([]);

  const [clientId, setClientId] = useState(presetClientId || "");
  const [projectName, setProjectName] = useState("");
  const [projectTypeId, setProjectTypeId] = useState("");
  const [metricKeys, setMetricKeys] = useState([]);
  const [goalTargets, setGoalTargets] = useState({});
  const [team, setTeam] = useState([]);

  useEffect(() => {
    loadReferenceData();
  }, []);

  async function loadReferenceData() {
    const [clientsRes, typesRes, metricsRes] = await Promise.all([
      supabase.from("clients").select("id, company_name").order("company_name"),
      supabase.from("project_types").select("*").order("created_at"),
      supabase.from("master_metrics").select("*").order("display_name"),
    ]);
    setClients(clientsRes.data || []);
    setProjectTypes(typesRes.data || []);
    setMasterMetrics(metricsRes.data || []);
  }

  function selectObjective(typeId) {
    setProjectTypeId(typeId);
    const type = projectTypes.find((t) => t.id === typeId);
    setMetricKeys(type ? [...type.default_metric_keys] : []);
  }

  function toggleMetric(key) {
    setMetricKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }

  function setGoal(key, value) {
    setGoalTargets((prev) => ({ ...prev, [key]: value }));
  }

  function addTeamRow() {
    setTeam((prev) => [...prev, { member_name: "", title: "", visible_to_client: true }]);
  }

  function updateTeamRow(index, patch) {
    setTeam((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function removeTeamRow(index) {
    setTeam((prev) => prev.filter((_, i) => i !== index));
  }

  function validateStep() {
    setError(null);
    if (step === 1 && (!clientId || !projectName.trim())) {
      setError("Pick a client and name the project.");
      return false;
    }
    if (step === 2 && !projectTypeId) {
      setError("Pick an objective.");
      return false;
    }
    return true;
  }

  function next() {
    if (!validateStep()) return;
    setStep((s) => Math.min(s + 1, STEPS.length));
  }

  function back() {
    setError(null);
    setStep((s) => Math.max(s - 1, 1));
  }

  async function finish() {
    if (!validateStep()) return;
    setSaving(true);
    setError(null);

    const cleanGoals = Object.fromEntries(
      Object.entries(goalTargets).filter(([, v]) => v !== "" && v !== null && v !== undefined)
    );

    const { data: project, error: projectError } = await supabase
      .from("projects")
      .insert({
        client_id: clientId,
        name: projectName.trim(),
        project_type_id: projectTypeId,
        metric_keys: metricKeys,
        goal_targets: cleanGoals,
      })
      .select()
      .single();

    if (projectError) {
      setSaving(false);
      setError(projectError.message);
      return;
    }

    if (team.length > 0) {
      const rows = team
        .filter((row) => row.member_name.trim() && row.title.trim())
        .map((row) => ({
          project_id: project.id,
          member_name: row.member_name.trim(),
          title: row.title.trim(),
          visible_to_client: row.visible_to_client,
        }));
      if (rows.length > 0) {
        const { error: teamError } = await supabase.from("project_team_members").insert(rows);
        if (teamError) {
          setSaving(false);
          setError(`Project was created, but team assignment failed: ${teamError.message}`);
          return;
        }
      }
    }

    setSaving(false);
    const objectiveName = projectTypes.find((t) => t.id === projectTypeId)?.name || "custom objective";
    logActivity({
      actorId: profile?.id,
      clientId,
      action: "Onboarded project",
      details: `${project.name}, ${objectiveName}`,
    });
    onSaved(project);
  }

  return (
    <ModalShell title="Add project" subtitle="Drives what the client sees for this project" onClose={onClose} wide>
      <div className="flex gap-1.5 mb-5">
        {STEPS.map((label, i) => (
          <button
            key={label}
            onClick={() => setStep(i + 1)}
            className="flex-1 flex flex-col gap-1.5 text-left"
          >
            <div className={`h-[3px] rounded-full ${i + 1 <= step ? "bg-primary" : "bg-accent"}`} />
            <span className={`font-mono text-[10px] uppercase tracking-wide ${i + 1 <= step ? "text-foreground" : "text-muted-foreground"}`}>
              {i + 1} {label}
            </span>
          </button>
        ))}
      </div>

      <ErrorBanner error={error} />

      {step === 1 && (
        <div>
          <Field label="Client">
            <SelectInput value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">Select a client</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.company_name}
                </option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Project name">
            <TextInput
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Northwind Retail: Google Ads"
            />
          </Field>
          <p className="text-[11px] text-muted-foreground">
            Don't see the client you need? Close this and add them first.
          </p>
        </div>
      )}

      {step === 2 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">Select an objective</p>
          <div className="grid grid-cols-2 gap-3">
            {projectTypes.map((t) => (
              <div
                key={t.id}
                onClick={() => selectObjective(t.id)}
                className={`border rounded-2xl p-4 cursor-pointer ${
                  projectTypeId === t.id ? "border-primary border-[1.5px] bg-accent" : "border-border"
                }`}
              >
                <div className="text-sm font-medium mb-1">{t.name}</div>
                <div className="text-xs text-muted-foreground">
                  {t.default_metric_keys.length === 0
                    ? "Pick any mix from the master list"
                    : t.default_metric_keys
                        .map((k) => masterMetrics.find((m) => m.key === k)?.display_name || k)
                        .join(", ")}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {step === 3 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">Metric set</p>
          <p className="text-[11px] text-muted-foreground mb-2">
            Auto-filled from the objective, uncheck any or add more.
          </p>
          <div className="flex flex-col gap-1 max-h-64 overflow-y-auto border border-border rounded-lg p-2">
            {masterMetrics.map((m) => (
              <label key={m.key} className="flex items-center gap-2 text-sm px-2 py-1.5">
                <input type="checkbox" checked={metricKeys.includes(m.key)} onChange={() => toggleMetric(m.key)} />
                {m.display_name}
                <span className="font-mono text-[10px] text-muted-foreground ml-auto">{m.unit}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {step === 4 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">Goal targets (optional)</p>
          <p className="text-[11px] text-muted-foreground mb-2">
            Leave blank to skip, used for the goal vs actual view.
          </p>
          {metricKeys.length === 0 ? (
            <p className="text-sm text-muted-foreground">No metrics selected yet.</p>
          ) : (
            metricKeys.map((key) => {
              const metric = masterMetrics.find((m) => m.key === key);
              return (
                <div key={key} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                  <span className="text-sm flex-1">{metric?.display_name || key}</span>
                  <input
                    className="w-32 border border-input rounded-lg px-2.5 py-1.5 text-sm text-right bg-background"
                    placeholder="target"
                    value={goalTargets[key] || ""}
                    onChange={(e) => setGoal(key, e.target.value)}
                  />
                </div>
              );
            })
          )}
        </div>
      )}

      {step === 5 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">Assign team</p>
          <p className="text-[11px] text-muted-foreground mb-2">Visible to the client unless you turn it off.</p>
          {team.map((row, i) => (
            <div key={i} className="flex items-center gap-2 py-2 border-b border-border last:border-0">
              <TextInput
                placeholder="Name, e.g. Priya Shah"
                value={row.member_name}
                onChange={(e) => updateTeamRow(i, { member_name: e.target.value })}
              />
              <TextInput
                placeholder="Title, e.g. Account executive"
                value={row.title}
                onChange={(e) => updateTeamRow(i, { title: e.target.value })}
              />
              <label className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={row.visible_to_client}
                  onChange={(e) => updateTeamRow(i, { visible_to_client: e.target.checked })}
                />
                Visible
              </label>
              <button onClick={() => removeTeamRow(i)} className="text-xs text-muted-foreground px-2">
                Remove
              </button>
            </div>
          ))}
          <button
            onClick={addTeamRow}
            className="text-xs border border-dashed border-input rounded-lg px-3 py-2 mt-2 w-full text-muted-foreground"
          >
            + Add team member
          </button>
        </div>
      )}

      <div className="flex justify-between mt-5 pt-4 border-t border-border">
        <button onClick={back} disabled={step === 1} className="text-sm text-muted-foreground px-3 py-2 disabled:opacity-0">
          Back
        </button>
        {step < STEPS.length ? (
          <button onClick={next} className="bg-primary text-primary-foreground rounded-lg px-5 py-2 text-sm font-semibold">
            Continue
          </button>
        ) : (
          <button
            onClick={finish}
            disabled={saving}
            className="bg-primary text-primary-foreground rounded-lg px-5 py-2 text-sm font-semibold disabled:opacity-60"
          >
            {saving ? "Saving..." : "Finish onboarding"}
          </button>
        )}
      </div>
    </ModalShell>
  );
}
